import { useCallback, useEffect, useRef } from 'react';
import { useOktaAuth } from '@okta/okta-react';

import { OKTA_LOGOUT_URL, OPAL_BACKEND_URL } from '@frontend/envConfig';
import { useInstanceId } from '@frontend/hooks';
import { sendSentryError } from '@frontend/utils/sendSentryError';

import { optiGlobalHeaderStyles } from './OptiGlobalHeader.css';

/**
 * Clears Opal session cookies on both backend and frontend domains.
 * Cookies are set on both (e.g. opal-backend.optimizely.com and
 * .opal.optimizely.com), so we need to target both domains.
 */
function clearSessionCookies(): Promise<Array<PromiseSettledResult<Response>>> {
  return Promise.allSettled([
    fetch(`${OPAL_BACKEND_URL}/api/v1/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    }),
    fetch('/api/v1/auth/logout', {
      method: 'POST',
      credentials: 'include',
    }),
  ]);
}

type OptiGlobalHeaderProps = {
  visible?: boolean;
};

export function OptiGlobalHeader({ visible = true }: OptiGlobalHeaderProps) {
  const { authState, oktaAuth } = useOktaAuth();
  const headerRef = useRef<HTMLDivElement>(null);
  const { instanceId } = useInstanceId();
  const onLogout = useCallback(() => {
    clearSessionCookies().finally(() =>
      oktaAuth
        .signOut({
          revokeAccessToken: true,
          revokeRefreshToken: true,
          postLogoutRedirectUri: OKTA_LOGOUT_URL,
        })
        .catch(err => sendSentryError(err))
    );
  }, [oktaAuth]);

  useEffect(() => {
    const currentHeaderRef = headerRef.current;
    currentHeaderRef?.addEventListener?.('@opti-common-header:logout', onLogout);

    return () => {
      currentHeaderRef?.removeEventListener?.('@opti-common-header:logout', onLogout);
    };
  }, [headerRef, onLogout]);
  if (!instanceId) {
    return null;
  }
  return (
    <div className={visible ? optiGlobalHeaderStyles.visible : optiGlobalHeaderStyles.hidden}>
      <opti-common-header
        ref={headerRef}
        auth-token={authState?.accessToken?.accessToken}
        product-name="Opal"
        instance-id={instanceId}
      />
    </div>
  );
}
